import { useState, useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminBrandsApi,
  adminCategoriesApi,
  adminCollectionsApi,
  adminReviewsApi,
  adminAttributesApi,
} from '../api/catalog';
import type {
  BrandUpsertDto,
  CategoryDto,
  CategoryUpsertDto,
  CollectionKind,
  CollectionRuleField,
  CollectionRuleOperator,
  CollectionUpsertDto,
  ReviewQueryDto,
  ReviewStatus,
  ProductAttributeDto,
  CategoryAttributeDto,
} from '../api/types';
import {
  collectionRuleFieldValues,
  collectionRuleOperatorValues,
  reviewStatusValues,
} from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import { Badge, ConfirmDialog, Field, FormErrors, Modal, PageLoader, Pagination, Spinner } from '../components/ui';
import { ImageUploadField } from '../components/ImageUploadField';
import { GoogleSearchPreview } from '../components/GoogleSearchPreview';
import { RichTextEditor } from '../components/RichTextEditor';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDate, number, truncate, slugify } from '../lib/format';
import {
  collectionRuleFieldLabels,
  collectionRuleOperatorLabels,
  labelOf,
  reviewStatusLabels,
} from '../lib/labels';
import { P } from '../lib/permissions';

/* ============================== KATEGORILER ============================== */

const emptyCategory: CategoryUpsertDto = {
  name: '',
  slug: '',
  description: '',
  parentId: null,
  isActive: true,
  showInMenu: true,
  isFeatured: false,
  displayOrder: 0,
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
};

export function CategoriesPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editing, setEditing] = useState<{
    id: number | null;
    form: CategoryUpsertDto;
    isSlugCustomized?: boolean;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [attrCategory, setAttrCategory] = useState<CategoryDto | null>(null);

  const categories = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => adminCategoriesApi.tree(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminCategoriesApi.create(editing!.form)
        : adminCategoriesApi.update(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Kategori kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminCategoriesApi.remove(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Kategori silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Kategori silinemedi.'),
  });

  const recalculate = useMutation({
    mutationFn: () => adminCategoriesApi.recalculateCounts(),
    onSuccess: (message) => {
      toast.success(message ?? 'Ürün sayaçları güncellendi.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Hesaplama yapılamadı.'),
  });

  const reorder = useMutation({
    mutationFn: (items: { id: number; parentId: number | null; displayOrder: number }[]) =>
      adminCategoriesApi.reorder({ items }),
    onSuccess: () => {
      toast.success('Sıralama güncellendi.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Sıralama kaydedilemedi.'),
  });

  if (categories.isLoading) return <PageLoader />;

  const tree = categories.data ?? [];

  /** Bir kategoriyi kardeşleri arasında yukarı/aşağı taşır. */
  const move = (siblings: CategoryDto[], index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= siblings.length) return;

    const reordered = [...siblings];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    reorder.mutate(
      reordered.map((category, position) => ({
        id: category.id,
        parentId: category.parentId ?? null,
        displayOrder: position,
      })),
    );
  };

  const renderRows = (nodes: CategoryDto[], depth = 0): JSX.Element[] =>
    nodes.flatMap((category, index) => [
      <tr key={category.id} className="hover:bg-ink-50">
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2" style={{ paddingLeft: depth * 20 }}>
            {depth > 0 ? <span className="text-ink-300">└</span> : null}
            <div className="h-8 w-8 shrink-0 overflow-hidden rounded bg-ink-50">
              {category.imageUrl ? (
                <img src={category.imageUrl} alt="" className="h-full w-full object-cover" />
              ) : null}
            </div>
            <div>
              <p className="font-medium text-ink-900">{category.name}</p>
              <p className="font-mono text-xs text-ink-400">{category.slug}</p>
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5 text-right text-ink-700">{number(category.productCount)}</td>
        <td className="hidden px-3 py-2.5 text-center md:table-cell">
          <div className="flex flex-wrap items-center justify-center gap-1">
            {category.showInMenu ? (
              <span className="badge bg-brand-50 text-brand-800">Menüde</span>
            ) : null}
            {category.isFeatured ? (
              <span className="badge bg-amber-50 text-amber-800 border border-amber-200">★ Ana Sayfada</span>
            ) : null}
            {!category.showInMenu && !category.isFeatured ? (
              <span className="text-ink-300">—</span>
            ) : null}
          </div>
        </td>
        <td className="px-3 py-2.5 text-center">
          {category.isActive ? (
            <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
          ) : (
            <span className="badge bg-ink-100 text-ink-600">Pasif</span>
          )}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => move(nodes, index, -1)}
              disabled={index === 0 || reorder.isPending}
              aria-label="Yukarı taşı"
            >
              ↑
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => move(nodes, index, 1)}
              disabled={index === nodes.length - 1 || reorder.isPending}
              aria-label="Aşağı taşı"
            >
              ↓
            </button>
            {can(P.products.update) ? (
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() =>
                  setEditing({
                    id: category.id,
                    isSlugCustomized: true,
                    form: {
                      name: category.name,
                      slug: category.slug,
                      description: category.description,
                      parentId: category.parentId ?? null,
                      imageUrl: category.imageUrl,
                      iconUrl: category.iconUrl,
                      bannerUrl: category.bannerUrl,
                      isActive: category.isActive,
                      showInMenu: category.showInMenu,
                      isFeatured: category.isFeatured,
                      displayOrder: category.displayOrder,
                      seoTitle: category.seoTitle,
                      seoDescription: category.seoDescription,
                      seoKeywords: category.seoKeywords,
                    },
                  })
                }
              >
                Düzenle
              </button>
            ) : null}
            {can(P.products.delete) ? (
              <button
                type="button"
                className="btn-ghost btn-sm text-red-600"
                onClick={() => setDeleteId(category.id)}
              >
                Sil
              </button>
            ) : null}
          </div>
        </td>
      </tr>,
      ...renderRows(category.children ?? [], depth + 1),
    ]);

  return (
    <div>
      <PageHeader
        title="Kategoriler"
        description="Vitrindeki gezinme ağacı"
        actions={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => recalculate.mutate()}
              disabled={recalculate.isPending}
            >
              {recalculate.isPending ? <Spinner className="h-4 w-4" /> : null}
              Sayaçları yenile
            </button>
            {can(P.products.create) ? (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setEditing({ id: null, form: emptyCategory, isSlugCustomized: false })}
              >
                Yeni kategori
              </button>
            ) : null}
          </>
        }
      />

      <div className="overflow-hidden rounded-xl border border-ink-200 bg-white">
        <div className="scroll-x">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-ink-200 bg-ink-50/70 text-left text-xs uppercase tracking-wide text-ink-500">
                <th className="px-3 py-2.5 font-semibold">Kategori</th>
                <th className="px-3 py-2.5 text-right font-semibold">Ürün</th>
                <th className="hidden px-3 py-2.5 text-center font-semibold md:table-cell">Menü</th>
                <th className="px-3 py-2.5 text-center font-semibold">Durum</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {tree.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-12 text-center text-sm text-ink-500">
                    Henüz kategori yok.
                  </td>
                </tr>
              ) : (
                renderRows(tree)
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni kategori' : 'Kategoriyi düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.name}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <Field label="Ad" htmlFor="cat-name" required>
              <input
                id="cat-name"
                className="field"
                value={editing.form.name}
                onChange={(event) => {
                  const newName = event.target.value;
                  setEditing((c) => c && {
                    ...c,
                    form: {
                      ...c.form,
                      name: newName,
                      slug: !c.isSlugCustomized ? slugify(newName) : c.form.slug,
                    },
                  });
                }}
                placeholder="Örn: Hurma Çeşitleri"
              />
            </Field>

            <Field
              label="URL adresi"
              htmlFor="cat-slug"
              hint={
                <span className="flex items-center justify-between text-xs">
                  <span>{!editing.isSlugCustomized ? 'Addan otomatik üretiliyor.' : 'Özel olarak düzenlendi.'}</span>
                  <button
                    type="button"
                    className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                    onClick={() =>
                      setEditing((c) => c && {
                        ...c,
                        isSlugCustomized: false,
                        form: { ...c.form, slug: slugify(c.form.name) },
                      })
                    }
                  >
                    Addan yeniden üret
                  </button>
                </span>
              }
            >
              <input
                id="cat-slug"
                className="field font-mono text-sm"
                value={editing.form.slug ?? ''}
                onChange={(event) =>
                  setEditing((c) => c && {
                    ...c,
                    isSlugCustomized: true,
                    form: { ...c.form, slug: event.target.value.toLowerCase().replace(/\s+/g, '-') },
                  })
                }
                onBlur={(event) => {
                  if (event.target.value) {
                    setEditing((c) => c && {
                      ...c,
                      form: { ...c.form, slug: slugify(event.target.value) },
                    });
                  }
                }}
                placeholder="hurma-cesitleri"
              />
            </Field>

            <Field label="Üst kategori" htmlFor="cat-parent">
              <select
                id="cat-parent"
                className="field"
                value={editing.form.parentId ?? ''}
                onChange={(event) =>
                  setEditing(
                    (c) =>
                      c && {
                        ...c,
                        form: {
                          ...c.form,
                          parentId: event.target.value ? Number(event.target.value) : null,
                        },
                      },
                  )
                }
              >
                <option value="">Kök kategori</option>
                {flatten(tree)
                  .filter((option) => option.id !== editing.id)
                  .map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label="Açıklama" htmlFor="cat-desc" hint="Kategori açıklamasını biçimlendirebilirsiniz.">
              <RichTextEditor
                id="cat-desc"
                value={editing.form.description ?? ''}
                onChange={(description) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, description } },
                  )
                }
                minHeight="min-h-[160px]"
                placeholder="Kategori açıklamasını girin..."
              />
            </Field>

            <ImageUploadField
              label="Kategori görseli"
              folder="categories"
              value={editing.form.imageUrl ?? ''}
              onChange={(url) =>
                setEditing((c) => c && { ...c, form: { ...c.form, imageUrl: url } })
              }
              hint="Önerilen boyut: 600x600 piksel"
            />

            <div className="grid gap-3 sm:grid-cols-3">
              <CheckboxField
                label="Aktif"
                checked={editing.form.isActive}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isActive: value } })
                }
              />
              <CheckboxField
                label="Menüde göster"
                checked={editing.form.showInMenu}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, showInMenu: value } })
                }
              />
              <CheckboxField
                label="Ana sayfada öne çıkar"
                checked={editing.form.isFeatured}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isFeatured: value } })
                }
              />
            </div>

            {/* SEO Bölümü */}
            <div className="pt-4 border-t border-ink-200 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">Arama Motoru Görünümü (SEO)</h3>
                <p className="mt-0.5 text-xs text-ink-500">
                  Kategorinin Google ve diğer arama motorlarında nasıl listeleneceğini özelleştirin.
                </p>
              </div>

              <GoogleSearchPreview
                title={editing.form.seoTitle}
                fallbackTitle={editing.form.name ? `${editing.form.name} Modelleri ve Fiyatları` : null}
                description={editing.form.seoDescription}
                fallbackDescription={editing.form.description}
                urlPath={`kategori/${editing.form.slug || 'kategori-adi'}`}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="SEO başlığı" htmlFor="cat-seo-title" hint="Boşsa otomatik üretilir.">
                  <input
                    id="cat-seo-title"
                    className="field"
                    maxLength={200}
                    value={editing.form.seoTitle ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoTitle: event.target.value } })
                    }
                  />
                </Field>

                <Field label="Anahtar kelimeler" htmlFor="cat-seo-keywords" hint="Virgülle ayırın.">
                  <input
                    id="cat-seo-keywords"
                    className="field"
                    value={editing.form.seoKeywords ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoKeywords: event.target.value } })
                    }
                  />
                </Field>
              </div>

              <Field label="SEO açıklaması" htmlFor="cat-seo-desc">
                <textarea
                  id="cat-seo-desc"
                  className="field min-h-20"
                  maxLength={320}
                  value={editing.form.seoDescription ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, seoDescription: event.target.value } })
                  }
                />
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Kategoriyi sil"
        message="Alt kategorisi veya ürünü olan kategori silinemez."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />

      {attrCategory && (
        <CategoryAttributesModal
          category={attrCategory}
          onClose={() => setAttrCategory(null)}
        />
      )}
    </div>
  );
}

function CategoryAttributesModal({
  category,
  onClose,
}: {
  category: CategoryDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const allAttributesQuery = useQuery<ProductAttributeDto[]>({
    queryKey: ['admin-attributes'],
    queryFn: () => adminAttributesApi.getAll(),
  });

  const categoryAttrsQuery = useQuery<CategoryAttributeDto[]>({
    queryKey: ['admin-category-attributes', category.id],
    queryFn: () => adminAttributesApi.getForCategory(category.id),
  });

  const [selected, setSelected] = useState<Map<number, { isRequired: boolean; isVariantOption: boolean; position: number }>>(new Map());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (categoryAttrsQuery.data && !initialized) {
      const map = new Map();
      categoryAttrsQuery.data.forEach((ca: CategoryAttributeDto, idx: number) => {
        map.set(ca.attributeId, {
          isRequired: ca.isRequired,
          isVariantOption: ca.isVariantOption,
          position: ca.position || idx + 1,
        });
      });
      setSelected(map);
      setInitialized(true);
    }
  }, [categoryAttrsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const assignments = Array.from(selected.entries()).map(([attrId, conf]) => ({
        attributeId: attrId,
        isRequired: conf.isRequired,
        isVariantOption: conf.isVariantOption,
        position: conf.position,
      }));
      return adminAttributesApi.assignToCategory(category.id, assignments);
    },
    onSuccess: () => {
      toast.success('Kategori özellikleri başarıyla güncellendi.');
      queryClient.invalidateQueries({ queryKey: ['admin-category-attributes', category.id] });
      onClose();
    },
    onError: () => {
      toast.error('Özellikler kaydedilirken bir hata oluştu.');
    },
  });

  const allAttrs = allAttributesQuery.data || [];

  const toggleAttribute = (attrId: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(attrId)) {
        next.delete(attrId);
      } else {
        next.set(attrId, { isRequired: false, isVariantOption: true, position: next.size + 1 });
      }
      return next;
    });
  };

  const updateConfig = (attrId: number, field: 'isRequired' | 'isVariantOption', val: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const cur = next.get(attrId);
      if (cur) {
        next.set(attrId, { ...cur, [field]: val });
      }
      return next;
    });
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Kategori Özellikleri: ${category.name}`}
    >
      <div className="space-y-4">
        <p className="text-xs text-ink-500">
          Bu kategoriye bağlanan özellikler vitrin filtrelerinde listelenir ve ürün varyantları oluştururken şablon olarak kullanılır.
        </p>

        {allAttributesQuery.isLoading || categoryAttrsQuery.isLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : allAttrs.length === 0 ? (
          <div className="py-6 text-center text-sm text-ink-500">
            Tanımlı özellik bulunamadı. Lütfen önce "Özellikler" sayfasından özellik tanımlayınız.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-ink-100">
            {allAttrs.map((attr: ProductAttributeDto) => {
              const isChecked = selected.has(attr.id);
              const conf = selected.get(attr.id);

              return (
                <div key={attr.id} className="py-3 flex items-center justify-between gap-3">
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleAttribute(attr.id)}
                      className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-ink-900">{attr.name}</span>
                      <span className="ml-2 text-xs text-ink-400 font-mono">({attr.code})</span>
                      <div className="text-xs text-ink-500 mt-0.5">
                        {attr.values?.length ?? 0} değer tanımlı
                      </div>
                    </div>
                  </label>

                  {isChecked && conf && (
                    <div className="flex items-center gap-4 text-xs">
                      <label className="flex items-center gap-1.5 cursor-pointer text-ink-600">
                        <input
                          type="checkbox"
                          checked={conf.isVariantOption}
                          onChange={(e) => updateConfig(attr.id, 'isVariantOption', e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-ink-300 text-brand-600"
                        />
                        Varyant Ekseni
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer text-ink-600">
                        <input
                          type="checkbox"
                          checked={conf.isRequired}
                          onChange={(e) => updateConfig(attr.id, 'isRequired', e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-ink-300 text-brand-600"
                        />
                        Zorunlu
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-ink-200">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary btn-sm"
          >
            Kapat
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="btn-primary btn-sm"
          >
            {saveMutation.isPending && <Spinner className="h-4 w-4" />}
            Kaydet
          </button>
        </div>
      </div>
    </Modal>
  );
}

function flatten(nodes: CategoryDto[], depth = 0): { id: number; label: string }[] {
  return nodes.flatMap((node) => [
    { id: node.id, label: `${'— '.repeat(depth)}${node.name}` },
    ...flatten(node.children ?? [], depth + 1),
  ]);
}

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-700">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-ink-300 text-brand-600"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

/* ================================ MARKALAR ================================ */

const emptyBrand: BrandUpsertDto = {
  name: '',
  slug: '',
  description: '',
  isActive: true,
  isFeatured: false,
  displayOrder: 0,
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
};

export function BrandsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{
    id: number | null;
    form: BrandUpsertDto;
    isSlugCustomized?: boolean;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const brands = useQuery({
    queryKey: ['admin', 'brands', page, search],
    queryFn: () => adminBrandsApi.search({ page, pageSize: 20, search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'brands'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminBrandsApi.create(editing!.form)
        : adminBrandsApi.update(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Marka kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminBrandsApi.remove(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Marka silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Marka silinemedi.'),
  });

  const columns: Column<NonNullable<typeof brands.data>['items'][number]>[] = [
    {
      key: 'name',
      header: 'Marka',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded bg-ink-50">
            {row.logoUrl ? (
              <img src={row.logoUrl} alt="" className="h-full w-full object-contain" />
            ) : (
              <span className="text-xs font-semibold text-ink-400">{row.name[0]}</span>
            )}
          </div>
          <div>
            <p className="font-medium text-ink-900">{row.name}</p>
            <p className="font-mono text-xs text-ink-400">{row.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'products',
      header: 'Ürün',
      align: 'right',
      cell: (row) => number(row.productCount),
    },
    {
      key: 'website',
      header: 'Web sitesi',
      hideOnMobile: true,
      cell: (row) =>
        row.websiteUrl ? (
          <a href={row.websiteUrl} target="_blank" rel="noreferrer" className="link text-xs">
            {truncate(row.websiteUrl, 32)}
          </a>
        ) : (
          <span className="text-ink-300">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Durum',
      align: 'center',
      cell: (row) => (
        <div className="flex flex-wrap items-center justify-center gap-1">
          {row.isActive ? (
            <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
          ) : (
            <span className="badge bg-ink-100 text-ink-600">Pasif</span>
          )}
          {row.isFeatured ? (
            <span className="badge bg-amber-50 text-amber-800 border border-amber-200">★ Ana Sayfada</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          {can(P.products.update) ? (
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() =>
                setEditing({
                  id: row.id,
                  isSlugCustomized: true,
                  form: {
                    name: row.name,
                    slug: row.slug,
                    description: row.description,
                    logoUrl: row.logoUrl,
                    bannerUrl: row.bannerUrl,
                    websiteUrl: row.websiteUrl,
                    countryCode: row.countryCode,
                    isActive: row.isActive,
                    isFeatured: row.isFeatured,
                    displayOrder: row.displayOrder,
                    seoTitle: row.seoTitle,
                    seoDescription: row.seoDescription,
                    seoKeywords: row.seoKeywords,
                  },
                })
              }
            >
              Düzenle
            </button>
          ) : null}
          {can(P.products.delete) ? (
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setDeleteId(row.id)}
            >
              Sil
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Markalar"
        actions={
          can(P.products.create) ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setEditing({ id: null, form: emptyBrand, isSlugCustomized: false })}
            >
              Yeni marka
            </button>
          ) : null
        }
      />

      <Toolbar>
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Marka adı"
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={brands.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={brands.isLoading}
        error={brands.error}
        onRetry={() => void brands.refetch()}
        emptyMessage="Marka bulunamadı."
        footer={
          brands.data ? (
            <Pagination
              page={brands.data.page}
              totalPages={brands.data.totalPages}
              totalCount={brands.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni marka' : 'Markayı düzenle'}
        onClose={() => setEditing(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.name}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <Field label="Ad" htmlFor="brand-name" required>
              <input
                id="brand-name"
                className="field"
                value={editing.form.name}
                onChange={(event) => {
                  const newName = event.target.value;
                  setEditing((c) => c && {
                    ...c,
                    form: {
                      ...c.form,
                      name: newName,
                      slug: !c.isSlugCustomized ? slugify(newName) : c.form.slug,
                    },
                  });
                }}
                placeholder="Örn: Medine Hurma Pazarı"
              />
            </Field>

            <Field
              label="URL adresi"
              htmlFor="brand-slug"
              hint={
                <span className="flex items-center justify-between text-xs">
                  <span>{!editing.isSlugCustomized ? 'Addan otomatik üretiliyor.' : 'Özel olarak düzenlendi.'}</span>
                  <button
                    type="button"
                    className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                    onClick={() =>
                      setEditing((c) => c && {
                        ...c,
                        isSlugCustomized: false,
                        form: { ...c.form, slug: slugify(c.form.name) },
                      })
                    }
                  >
                    Addan yeniden üret
                  </button>
                </span>
              }
            >
              <input
                id="brand-slug"
                className="field font-mono text-sm"
                value={editing.form.slug ?? ''}
                onChange={(event) =>
                  setEditing((c) => c && {
                    ...c,
                    isSlugCustomized: true,
                    form: { ...c.form, slug: event.target.value.toLowerCase().replace(/\s+/g, '-') },
                  })
                }
                onBlur={(event) => {
                  if (event.target.value) {
                    setEditing((c) => c && {
                      ...c,
                      form: { ...c.form, slug: slugify(event.target.value) },
                    });
                  }
                }}
                placeholder="medine-hurma-pazari"
              />
            </Field>

            <Field label="Açıklama" htmlFor="brand-desc">
              <textarea
                id="brand-desc"
                className="field min-h-20"
                value={editing.form.description ?? ''}
                onChange={(event) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, description: event.target.value } },
                  )
                }
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <ImageUploadField
                label="Marka logosu"
                folder="brands"
                value={editing.form.logoUrl ?? ''}
                onChange={(url) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, logoUrl: url } })
                }
                hint="Önerilen boyut: 400x200 piksel"
              />
              <Field label="Web sitesi" htmlFor="brand-web">
                <input
                  id="brand-web"
                  type="url"
                  className="field"
                  value={editing.form.websiteUrl ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, websiteUrl: event.target.value } },
                    )
                  }
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <CheckboxField
                label="Aktif"
                checked={editing.form.isActive}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isActive: value } })
                }
              />
              <CheckboxField
                label="Ana sayfada öne çıkar"
                checked={editing.form.isFeatured}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isFeatured: value } })
                }
              />
            </div>

            {/* SEO Bölümü */}
            <div className="pt-4 border-t border-ink-200 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">Arama Motoru Görünümü (SEO)</h3>
                <p className="mt-0.5 text-xs text-ink-500">
                  Markanın Google ve diğer arama motorlarında nasıl listeleneceğini özelleştirin.
                </p>
              </div>

              <GoogleSearchPreview
                title={editing.form.seoTitle}
                fallbackTitle={editing.form.name ? `${editing.form.name} Ürünleri ve Fiyatları` : null}
                description={editing.form.seoDescription}
                fallbackDescription={editing.form.description}
                urlPath={`marka/${editing.form.slug || 'marka-adi'}`}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="SEO başlığı" htmlFor="brand-seo-title" hint="Boşsa otomatik üretilir.">
                  <input
                    id="brand-seo-title"
                    className="field"
                    maxLength={200}
                    value={editing.form.seoTitle ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoTitle: event.target.value } })
                    }
                  />
                </Field>

                <Field label="Anahtar kelimeler" htmlFor="brand-seo-keywords" hint="Virgülle ayırın.">
                  <input
                    id="brand-seo-keywords"
                    className="field"
                    value={editing.form.seoKeywords ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoKeywords: event.target.value } })
                    }
                  />
                </Field>
              </div>

              <Field label="SEO açıklaması" htmlFor="brand-seo-desc">
                <textarea
                  id="brand-seo-desc"
                  className="field min-h-20"
                  maxLength={320}
                  value={editing.form.seoDescription ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, seoDescription: event.target.value } })
                  }
                />
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Markayı sil"
        message="Bu markaya bağlı ürün varsa silme işlemi reddedilir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ============================== KOLEKSIYONLAR ============================== */

const emptyCollection: CollectionUpsertDto = {
  title: '',
  slug: '',
  description: '',
  kind: 'Manual',
  matchAllRules: true,
  isActive: true,
  isFeatured: false,
  displayOrder: 0,
  sortOrder: 'manual',
  productIds: [],
  rules: [],
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
};

export function CollectionsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{
    id: number | null;
    form: CollectionUpsertDto;
    isSlugCustomized?: boolean;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const collections = useQuery({
    queryKey: ['admin', 'collections', page, search],
    queryFn: () => adminCollectionsApi.search({ page, pageSize: 20, search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'collections'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminCollectionsApi.create(editing!.form)
        : adminCollectionsApi.update(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Koleksiyon kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminCollectionsApi.remove(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Koleksiyon silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Koleksiyon silinemedi.'),
  });

  const refresh = useMutation({
    mutationFn: (id: number) => adminCollectionsApi.refresh(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Koleksiyon üyeleri yeniden hesaplandı.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Yenileme başarısız.'),
  });

  const columns: Column<NonNullable<typeof collections.data>['items'][number]>[] = [
    {
      key: 'title',
      header: 'Koleksiyon',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded bg-ink-50">
            {row.imageUrl ? (
              <img src={row.imageUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-xs font-semibold text-ink-400">{row.title[0]}</span>
            )}
          </div>
          <div>
            <p className="font-medium text-ink-900">{row.title}</p>
            <p className="font-mono text-xs text-ink-400">{row.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Tür',
      cell: (row) => (
        <span className="badge bg-ink-100 text-ink-700">
          {row.kind === 'Automated' ? 'Kurallı' : 'Elle seçim'}
        </span>
      ),
    },
    {
      key: 'products',
      header: 'Ürün',
      align: 'right',
      cell: (row) => number(row.productCount),
    },
    {
      key: 'refreshed',
      header: 'Son yenileme',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{formatDate(row.lastRefreshedAt)}</span>,
    },
    {
      key: 'status',
      header: 'Durum',
      align: 'center',
      cell: (row) => (
        <div className="flex flex-wrap items-center justify-center gap-1">
          {row.isActive ? (
            <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
          ) : (
            <span className="badge bg-ink-100 text-ink-600">Pasif</span>
          )}
          {row.isFeatured ? (
            <span className="badge bg-amber-50 text-amber-800 border border-amber-200">★ Ana Sayfada</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          {row.kind === 'Automated' ? (
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => refresh.mutate(row.id)}
              disabled={refresh.isPending}
            >
              Yenile
            </button>
          ) : null}
          {can(P.products.update) ? (
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() =>
                setEditing({
                  id: row.id,
                  isSlugCustomized: true,
                  form: {
                    title: row.title,
                    slug: row.slug,
                    description: row.description,
                    kind: row.kind,
                    matchAllRules: true,
                    imageUrl: row.imageUrl,
                    bannerUrl: row.bannerUrl,
                    isActive: row.isActive,
                    isFeatured: row.isFeatured,
                    displayOrder: row.displayOrder,
                    publishedAt: row.publishedAt,
                    expiresAt: row.expiresAt,
                    sortOrder: row.sortOrder,
                    productIds: [],
                    rules: row.rules.map((rule, index) => ({
                      field: rule.field,
                      operator: rule.operator,
                      value: rule.value,
                      position: index,
                    })),
                    seoTitle: row.seoTitle,
                    seoDescription: row.seoDescription,
                    seoKeywords: row.seoKeywords,
                  },
                })
              }
            >
              Düzenle
            </button>
          ) : null}
          {can(P.products.delete) ? (
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setDeleteId(row.id)}
            >
              Sil
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Koleksiyonlar"
        description="Elle seçim veya kural tabanlı ürün grupları"
        actions={
          can(P.products.create) ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setEditing({ id: null, form: emptyCollection, isSlugCustomized: false })}
            >
              Yeni koleksiyon
            </button>
          ) : null
        }
      />

      <Toolbar>
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Koleksiyon adı"
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={collections.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={collections.isLoading}
        error={collections.error}
        onRetry={() => void collections.refetch()}
        emptyMessage="Koleksiyon bulunamadı."
        footer={
          collections.data ? (
            <Pagination
              page={collections.data.page}
              totalPages={collections.data.totalPages}
              totalCount={collections.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni koleksiyon' : 'Koleksiyonu düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.title}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <Field label="Başlık" htmlFor="col-title" required>
              <input
                id="col-title"
                className="field"
                value={editing.form.title}
                onChange={(event) => {
                  const newTitle = event.target.value;
                  setEditing((c) => c && {
                    ...c,
                    form: {
                      ...c.form,
                      title: newTitle,
                      slug: !c.isSlugCustomized ? slugify(newTitle) : c.form.slug,
                    },
                  });
                }}
                placeholder="Örn: Ramazan Özel Koleksiyonu"
              />
            </Field>

            <Field
              label="URL adresi (slug)"
              htmlFor="col-slug"
              hint={
                <span className="flex items-center justify-between text-xs">
                  <span>{!editing.isSlugCustomized ? 'Başlıktan otomatik üretiliyor.' : 'Özel olarak düzenlendi.'}</span>
                  <button
                    type="button"
                    className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                    onClick={() =>
                      setEditing((c) => c && {
                        ...c,
                        isSlugCustomized: false,
                        form: { ...c.form, slug: slugify(c.form.title) },
                      })
                    }
                  >
                    Başlıktan yeniden üret
                  </button>
                </span>
              }
            >
              <input
                id="col-slug"
                className="field font-mono text-sm"
                value={editing.form.slug ?? ''}
                onChange={(event) =>
                  setEditing((c) => c && {
                    ...c,
                    isSlugCustomized: true,
                    form: { ...c.form, slug: event.target.value.toLowerCase().replace(/\s+/g, '-') },
                  })
                }
                onBlur={(event) => {
                  if (event.target.value) {
                    setEditing((c) => c && {
                      ...c,
                      form: { ...c.form, slug: slugify(event.target.value) },
                    });
                  }
                }}
                placeholder="ramazan-ozel-koleksiyonu"
              />
            </Field>

            <Field label="Açıklama" htmlFor="col-desc" hint="Koleksiyon açıklamasını biçimlendirebilirsiniz.">
              <RichTextEditor
                id="col-desc"
                value={editing.form.description ?? ''}
                onChange={(description) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, description } },
                  )
                }
                minHeight="min-h-[160px]"
                placeholder="Koleksiyon açıklamasını girin..."
              />
            </Field>

            <ImageUploadField
              label="Koleksiyon kapak görseli"
              folder="collections"
              value={editing.form.imageUrl ?? ''}
              onChange={(url) =>
                setEditing((c) => c && { ...c, form: { ...c.form, imageUrl: url } })
              }
              hint="Ana sayfada öne çıkarıldığında gösterilecek görsel (Önerilen: 800x500 piksel)"
            />

            <Field label="Tür" htmlFor="col-kind">
              <select
                id="col-kind"
                className="field"
                value={editing.form.kind}
                onChange={(event) =>
                  setEditing(
                    (c) =>
                      c && {
                        ...c,
                        form: { ...c.form, kind: event.target.value as CollectionKind },
                      },
                  )
                }
              >
                <option value="Manual">Elle seçim</option>
                <option value="Automated">Kurallı (otomatik)</option>
              </select>
            </Field>

            {editing.form.kind === 'Automated' ? (
              <div className="rounded-lg border border-ink-200 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-ink-900">Eşleşme kuralları</h3>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() =>
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: {
                              ...c.form,
                              rules: [
                                ...c.form.rules,
                                {
                                  field: 'Title',
                                  operator: 'Contains',
                                  value: '',
                                  position: c.form.rules.length,
                                },
                              ],
                            },
                          },
                      )
                    }
                  >
                    Kural ekle
                  </button>
                </div>

                <label className="mb-3 flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="radio"
                    className="h-4 w-4 border-ink-300 text-brand-600"
                    checked={editing.form.matchAllRules}
                    onChange={() =>
                      setEditing((c) => c && { ...c, form: { ...c.form, matchAllRules: true } })
                    }
                  />
                  Tüm kurallar sağlanmalı (VE)
                </label>
                <label className="mb-4 flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="radio"
                    className="h-4 w-4 border-ink-300 text-brand-600"
                    checked={!editing.form.matchAllRules}
                    onChange={() =>
                      setEditing((c) => c && { ...c, form: { ...c.form, matchAllRules: false } })
                    }
                  />
                  Herhangi biri yeterli (VEYA)
                </label>

                {editing.form.rules.length === 0 ? (
                  <p className="text-sm text-ink-500">Henüz kural eklenmedi.</p>
                ) : (
                  <ul className="space-y-2">
                    {editing.form.rules.map((rule, index) => (
                      <li key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                        <select
                          className="field py-2 text-sm"
                          value={rule.field}
                          aria-label="Alan"
                          onChange={(event) =>
                            setEditing(
                              (c) =>
                                c && {
                                  ...c,
                                  form: {
                                    ...c.form,
                                    rules: c.form.rules.map((item, i) =>
                                      i === index
                                        ? {
                                            ...item,
                                            field: event.target.value as CollectionRuleField,
                                          }
                                        : item,
                                    ),
                                  },
                                },
                            )
                          }
                        >
                          {collectionRuleFieldValues.map((value) => (
                            <option key={value} value={value}>
                              {collectionRuleFieldLabels[value]}
                            </option>
                          ))}
                        </select>

                        <select
                          className="field py-2 text-sm"
                          value={rule.operator}
                          aria-label="Operatör"
                          onChange={(event) =>
                            setEditing(
                              (c) =>
                                c && {
                                  ...c,
                                  form: {
                                    ...c.form,
                                    rules: c.form.rules.map((item, i) =>
                                      i === index
                                        ? {
                                            ...item,
                                            operator: event.target
                                              .value as CollectionRuleOperator,
                                          }
                                        : item,
                                    ),
                                  },
                                },
                            )
                          }
                        >
                          {collectionRuleOperatorValues.map((value) => (
                            <option key={value} value={value}>
                              {collectionRuleOperatorLabels[value]}
                            </option>
                          ))}
                        </select>

                        <input
                          className="field py-2 text-sm"
                          placeholder="Değer"
                          aria-label="Değer"
                          value={rule.value}
                          onChange={(event) =>
                            setEditing(
                              (c) =>
                                c && {
                                  ...c,
                                  form: {
                                    ...c.form,
                                    rules: c.form.rules.map((item, i) =>
                                      i === index ? { ...item, value: event.target.value } : item,
                                    ),
                                  },
                                },
                            )
                          }
                        />

                        <button
                          type="button"
                          className="btn-ghost btn-sm text-red-600"
                          onClick={() =>
                            setEditing(
                              (c) =>
                                c && {
                                  ...c,
                                  form: {
                                    ...c.form,
                                    rules: c.form.rules.filter((_, i) => i !== index),
                                  },
                                },
                            )
                          }
                        >
                          Sil
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <Field
                label="Ürün kimlikleri"
                htmlFor="col-products"
                hint="Virgülle ayırın. Ürün listesinden kimlikleri kopyalayabilirsiniz."
              >
                <input
                  id="col-products"
                  className="field font-mono text-sm"
                  value={editing.form.productIds.join(', ')}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            productIds: event.target.value
                              .split(',')
                              .map((raw) => Number(raw.trim()))
                              .filter((value) => Number.isFinite(value) && value > 0),
                          },
                        },
                    )
                  }
                />
              </Field>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <CheckboxField
                label="Aktif"
                checked={editing.form.isActive}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isActive: value } })
                }
              />
              <CheckboxField
                label="Ana sayfada öne çıkar"
                checked={editing.form.isFeatured}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isFeatured: value } })
                }
              />
            </div>

            {/* SEO Bölümü */}
            <div className="pt-4 border-t border-ink-200 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">Arama Motoru Görünümü (SEO)</h3>
                <p className="mt-0.5 text-xs text-ink-500">
                  Koleksiyonun Google ve diğer arama motorlarında nasıl listeleneceğini özelleştirin.
                </p>
              </div>

              <GoogleSearchPreview
                title={editing.form.seoTitle}
                fallbackTitle={editing.form.title ? `${editing.form.title} Fırsatları` : null}
                description={editing.form.seoDescription}
                fallbackDescription={editing.form.description}
                urlPath={`koleksiyon/${editing.form.slug || 'koleksiyon-adi'}`}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="SEO başlığı" htmlFor="col-seo-title" hint="Boşsa otomatik üretilir.">
                  <input
                    id="col-seo-title"
                    className="field"
                    maxLength={200}
                    value={editing.form.seoTitle ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoTitle: event.target.value } })
                    }
                  />
                </Field>

                <Field label="Anahtar kelimeler" htmlFor="col-seo-keywords" hint="Virgülle ayırın.">
                  <input
                    id="col-seo-keywords"
                    className="field"
                    value={editing.form.seoKeywords ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoKeywords: event.target.value } })
                    }
                  />
                </Field>
              </div>

              <Field label="SEO açıklaması" htmlFor="col-seo-desc">
                <textarea
                  id="col-seo-desc"
                  className="field min-h-20"
                  maxLength={320}
                  value={editing.form.seoDescription ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, seoDescription: event.target.value } })
                  }
                />
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Koleksiyonu sil"
        message="Koleksiyon silinir; içindeki ürünler etkilenmez."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ================================ YORUMLAR ================================ */

export function ReviewsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState<ReviewQueryDto>({ page: 1, pageSize: 20 });
  const [replyTo, setReplyTo] = useState<{ id: number; text: string } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const reviews = useQuery({
    queryKey: ['admin', 'reviews', query],
    queryFn: () => adminReviewsApi.search(query),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });

  const moderate = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ReviewStatus }) =>
      adminReviewsApi.moderate(id, { status }),
    onSuccess: () => {
      toast.success('Yorum durumu güncellendi.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  const reply = useMutation({
    mutationFn: () => adminReviewsApi.reply(replyTo!.id, { reply: replyTo!.text }),
    onSuccess: () => {
      toast.success('Yanıt kaydedildi.');
      setReplyTo(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminReviewsApi.remove(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Yorum silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Yorum silinemedi.'),
  });

  return (
    <div>
      <PageHeader title="Yorumlar" description="Müşteri değerlendirmelerini yönetin" />

      <Toolbar>
        <SelectFilter
          label="Durum"
          value={query.status ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, status: (value || undefined) as ReviewStatus, page: 1 }))
          }
          options={reviewStatusValues.map((value) => ({
            value,
            label: reviewStatusLabels[value].text,
          }))}
        />

        <SelectFilter
          label="Puan"
          value={query.rating ? String(query.rating) : ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, rating: value ? Number(value) : undefined, page: 1 }))
          }
          options={[5, 4, 3, 2, 1].map((value) => ({ value: String(value), label: `${value} yıldız` }))}
        />

        <label className="mb-1 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={query.verifiedOnly === true}
            onChange={(event) =>
              setQuery((c) => ({ ...c, verifiedOnly: event.target.checked || undefined, page: 1 }))
            }
          />
          Yalnızca doğrulanmış alışveriş
        </label>
      </Toolbar>

      {reviews.isLoading ? (
        <PageLoader />
      ) : (
        <div className="space-y-4">
          {(reviews.data?.items ?? []).map((review) => (
            <article key={review.id} className="card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-amber-500" aria-label={`${review.rating} yıldız`}>
                      {'★'.repeat(review.rating)}
                      <span className="text-ink-200">{'★'.repeat(5 - review.rating)}</span>
                    </span>
                    <span className="text-sm font-medium text-ink-900">{review.authorName}</span>
                    {review.isVerifiedPurchase ? (
                      <span className="badge bg-emerald-50 text-emerald-700">Doğrulanmış</span>
                    ) : null}
                    <Badge label={labelOf(reviewStatusLabels, review.status)} />
                  </div>

                  <p className="mt-1 text-xs text-ink-400">
                    {review.productTitle ?? `Ürün #${review.productId}`} ·{' '}
                    {formatDate(review.createdAt)}
                    {review.authorEmail ? ` · ${review.authorEmail}` : ''} ·{' '}
                    <span className="text-emerald-600 font-medium">👍 {review.helpfulCount}</span> ·{' '}
                    <span className="text-rose-600 font-medium">👎 {review.notHelpfulCount}</span>
                  </p>
                </div>

                <div className="flex flex-wrap gap-1">
                  {review.status !== 'Approved' ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => moderate.mutate({ id: review.id, status: 'Approved' })}
                    >
                      Onayla
                    </button>
                  ) : null}
                  {review.status !== 'Rejected' ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => moderate.mutate({ id: review.id, status: 'Rejected' })}
                    >
                      Reddet
                    </button>
                  ) : null}
                  {review.status !== 'Spam' ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => moderate.mutate({ id: review.id, status: 'Spam' })}
                    >
                      Spam
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => setReplyTo({ id: review.id, text: review.storeReply ?? '' })}
                  >
                    Yanıtla
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-sm text-red-600"
                    onClick={() => setDeleteId(review.id)}
                  >
                    Sil
                  </button>
                </div>
              </div>

              {review.title ? (
                <h3 className="mt-3 text-sm font-semibold text-ink-900">{review.title}</h3>
              ) : null}
              <p className="mt-1.5 text-sm leading-6 text-ink-600">{review.comment}</p>

              {review.imageUrls.length > 0 ? (
                <div className="mt-3 flex gap-2">
                  {review.imageUrls.map((url) => (
                    <img key={url} src={url} alt="" className="h-16 w-16 rounded object-cover" />
                  ))}
                </div>
              ) : null}

              {review.storeReply ? (
                <div className="mt-3 rounded-lg bg-ink-50 px-4 py-3">
                  <p className="text-xs font-medium text-ink-700">Mağaza yanıtı</p>
                  <p className="mt-1 text-sm text-ink-600">{review.storeReply}</p>
                </div>
              ) : null}
            </article>
          ))}

          {reviews.data && reviews.data.items.length === 0 ? (
            <p className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
              Bu filtrelere uyan yorum yok.
            </p>
          ) : null}

          {reviews.data ? (
            <Pagination
              page={reviews.data.page}
              totalPages={reviews.data.totalPages}
              totalCount={reviews.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null}
        </div>
      )}

      <Modal
        open={replyTo !== null}
        title="Yoruma yanıt"
        description="Yanıtınız yorumun altında herkese görünür."
        onClose={() => setReplyTo(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setReplyTo(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => reply.mutate()}
              disabled={reply.isPending || !replyTo?.text}
            >
              {reply.isPending ? <Spinner className="h-4 w-4" /> : null}
              Yanıtla
            </button>
          </>
        }
      >
        <FormErrors error={reply.error} />
        <Field label="Yanıt" htmlFor="review-reply" required>
          <textarea
            id="review-reply"
            className="field min-h-28"
            maxLength={2000}
            value={replyTo?.text ?? ''}
            onChange={(event) => setReplyTo((c) => c && { ...c, text: event.target.value })}
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Yorumu sil"
        message="Yorum kalıcı olarak silinecek."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}
